import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconSearch,
  IconCommand,
  IconArrowRight,
  IconX,
  IconLayoutDashboard,
  IconUser,
  IconId,
  IconBuilding,
  IconCash,
  IconClipboardList,
  IconShoppingCart,
  IconFileDescription,
  IconClipboardData,
  IconCalendar,
  IconChartPie,
  IconSettings,
  IconInbox,
  // IconLayers,
  IconSitemap,
} from "@tabler/icons-react";

const SEARCH_ITEMS = [
  { id: "p-dash", title: "Dashboard", category: "Pages", to: "/dashboard", icon: IconLayoutDashboard },
  { id: "p-leads", title: "Leads", category: "Pages", to: "/leads", icon: IconUser },
  { id: "p-contacts", title: "Contacts", category: "Pages", to: "/contacts", icon: IconId },
  { id: "p-company", title: "Company", category: "Pages", to: "/company", icon: IconBuilding },
  { id: "p-deals", title: "Deals", category: "Pages", to: "/deals", icon: IconCash },
  { id: "p-quote", title: "Quotations", category: "Pages", to: "/quotations", icon: IconClipboardList },
  { id: "p-po", title: "Purchase Orders", category: "Pages", to: "/purchaseorder", icon: IconShoppingCart },
  { id: "p-so", title: "Sales Orders", category: "Pages", to: "/salesorder", icon: IconFileDescription },
  { id: "p-projects", title: "Projects", category: "Pages", to: "/projects", icon: IconClipboardData },
  { id: "p-project-view", title: "Projects View", category: "Pages", to: "/ProjectDashboard", icon: IconClipboardData },
  { id: "p-inbox", title: "Inbox", category: "Pages", to: "/inbox", icon: IconInbox },
  { id: "p-workflow", title: "Workflows", category: "Pages", to: "/workflow", icon: IconSitemap },
  { id: "p-calendar", title: "Calendar", category: "Pages", to: "/calender", icon: IconCalendar },
  { id: "p-reports", title: "Reports", category: "Pages", to: "/reports", icon: IconChartPie },
  { id: "p-settings", title: "Settings", category: "Pages", to: "/settings/masters", icon: IconSettings },
];

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (
        e.key === "/" &&
        !open &&
        !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)
      ) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const filtered = SEARCH_ITEMS.filter(
    (item) =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.category.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, open]);

  const handleSelect = (item) => {
    setOpen(false);
    setQuery("");
    setSelectedIndex(0);
    navigate(item.to);
  };

  useEffect(() => {
    if (!open) return undefined;

    function handleNav(e) {
      const items = SEARCH_ITEMS.filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.category.toLowerCase().includes(query.toLowerCase())
      );

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) =>
          items.length ? Math.min(i + 1, items.length - 1) : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const item = items[selectedIndex];
        if (!item) return;
        e.preventDefault();
        setOpen(false);
        setQuery("");
        setSelectedIndex(0);
        navigate(item.to);
      }
    }

    window.addEventListener("keydown", handleNav);
    return () => window.removeEventListener("keydown", handleNav);
  }, [open, query, selectedIndex, navigate]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="crm-gs__trigger"
      >
        <IconSearch size={15} className="crm-gs__trigger-icon" />
        <span className="crm-gs__trigger-text">
          Search leads, contacts, modules...
        </span>
        <kbd className="crm-gs__trigger-kbd">
          <IconCommand size={10} />
          <span>K</span>
        </kbd>
      </button>

      {open && (
        <div className="crm-gs__overlay">
          <div className="crm-gs__backdrop" onClick={() => setOpen(false)} />

          <div className="crm-gs__panel">
            <div className="crm-gs__input-row">
              <IconSearch size={18} className="crm-gs__input-icon" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                placeholder="Type to search modules, leads, contacts, or orders..."
                className="crm-gs__input"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="crm-gs__clear"
                  aria-label="Clear search"
                >
                  <IconX size={15} />
                </button>
              ) : (
                <kbd className="crm-gs__esc">ESC</kbd>
              )}
            </div>

            <div className="crm-gs__results crm-shell-scroll">
              {filtered.length === 0 ? (
                <div className="crm-gs__empty">
                  <p className="crm-gs__empty-title">
                    No results found for &quot;{query}&quot;
                  </p>
                  <p className="crm-gs__empty-sub">
                    Try searching for leads, contacts, deals, or settings.
                  </p>
                </div>
              ) : (
                <div className="crm-gs__list">
                  {filtered.map((item, index) => {
                    const Icon = item.icon;
                    const active = index === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`crm-gs__item${active ? " crm-gs__item--active" : ""}`}
                      >
                        <div className="crm-gs__item-left">
                          <div
                            className={`crm-gs__item-icon${active ? " crm-gs__item-icon--active" : ""
                              }`}
                          >
                            <Icon size={15} />
                          </div>
                          <div>
                            <p className="crm-gs__item-title">{item.title}</p>
                            <p className="crm-gs__item-cat">{item.category}</p>
                          </div>
                        </div>
                        <IconArrowRight size={13} className="crm-gs__item-arrow" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="crm-gs__footer">
              <div className="crm-gs__footer-hints">
                <span>
                  Navigate with{" "}
                  <kbd className="crm-gs__footer-kbd">↑</kbd>{" "}
                  <kbd className="crm-gs__footer-kbd">↓</kbd>
                </span>
                <span>
                  Select with <kbd className="crm-gs__footer-kbd">↵</kbd>
                </span>
              </div>
              <span>
                Press <kbd className="crm-gs__footer-kbd">ESC</kbd> to close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
